import { useState, useEffect, useCallback } from "react";
import superagent from "superagent";
import { Button } from "@shadcn/components/ui/button";
import { Input } from "@shadcn/components/ui/input";
import { Label } from "@shadcn/components/ui/label";
import { Filter, Download, Search } from 'lucide-react';
import * as moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { backendUrl } from '@helpers/consts';

// Import new components
import WeeklySalesHeader from './components/WeeklySalesHeader';
import WeeklySalesTable from './components/WeeklySalesTable';
import WeeklySalesFilterModal from './components/WeeklySalesFilterModal';
import WeeklySalesExport from './components/WeeklySalesExport';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const WeeklySalesReport = () => {
  // Helper function to get default date range (current week)
  const getDefaultDateRange = () => {
    const now = new Date();
    const startOfWeek = new Date(now);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday
    startOfWeek.setDate(diff);
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6); // Sunday
    endOfWeek.setHours(23, 59, 59, 999);

    return { startOfWeek, endOfWeek };
  };

  const { startOfWeek, endOfWeek } = getDefaultDateRange();

  const [loading, setLoading] = useState(true);
  const [salesData, setSalesData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  const [filters, setFilters] = useState({
    regionConfigs: ["OnlineAU"],
    startDate: startOfWeek,
    endDate: endOfWeek,
    categoryNames: [],
    productNames: [],
    productIds: []
  });
  

  
  const [regionConfigOptions, setRegionConfigOptions] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchSalesData();
  }, [filters]);

  const fetchInitialData = async () => {
    try {
      await fetchRegionConfigs();
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setLoading(false);
    }
  };

  const fetchRegionConfigs = async () => {
    try {
      const response = await superagent
        .get(`${backendUrl}/regionConfigs`)
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json')
        .query({includeShopify: true});
      
      const sources = response.body;
      const uniqueRegionConfigs = [...new Set(sources
        .filter(source => source.isActive)
        .map(source => ({
          label: source.regionId,
          value: source.regionId
        })))];
      
      setRegionConfigOptions(uniqueRegionConfigs);
      
      // Set default if current selection is not valid
      if (uniqueRegionConfigs.length > 0 && !uniqueRegionConfigs.find(source => source.value === filters.regionConfigs[0])) {
        setFilters(prev => ({
          ...prev,
          regionConfigs: [uniqueRegionConfigs[0].value]
        }));
      }
    } catch (err) {
      console.error("Error fetching region configs:", err);
    }
  };

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      
      // Prepare payload for the API
      const payload = {
        regionConfigs: filters.regionConfigs,
        startDate: filters.startDate.toISOString(),
        endDate: filters.endDate.toISOString()
      };

      // Add category/product filters if they exist
      if (filters.categoryNames && filters.categoryNames.length > 0) {
        payload.categoryNames = filters.categoryNames;
      }
      
      if (filters.productIds && filters.productIds.length > 0) {
        payload.productIds = filters.productIds;
      }
      
      const response = await superagent
        .post(`${backendUrl}/reports/sku-weekly`)
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json')
        .send(payload);
      
      setSalesData(response.body);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching sales data:", err);
      setLoading(false);
    }
  };

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await WeeklySalesExport.exportToExcel(salesData, filters, searchTerm);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const activeFiltersCount = (filters.categoryNames?.length || 0) + (filters.productIds?.length || 0);
  const hasActiveFilters = activeFiltersCount > 0;


  if (loading && !salesData) {
    return (
      <div className="page-content">
        <div className="container mx-auto p-4">
          <div className="flex justify-center items-center h-screen">
            <div className="flex flex-col items-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              <span className="mt-2 text-sm text-gray-500">Loading weekly sales data...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  
  return (
    <div className="page-content"> 
      <div className="container mx-auto p-4">
        <WeeklySalesHeader />
        
        {/* Filter and Export Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Search Input */}
          <div className="flex-1 max-w-md">
            <Label htmlFor="search" className="text-sm font-medium text-gray-700">
              Search SKUs
            </Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="search"
                type="text"
                placeholder="Search by SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Filter and Export Buttons */}
          <div className="flex gap-2 items-end">
            <Button
              onClick={() => setIsFilterModalOpen(true)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
            
            <Button
              onClick={handleExport}
              variant="outline"
              disabled={!salesData || isExporting}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200 animate-fade-in">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-blue-900">Active Filters:</h4>
                             <Button 
                 onClick={() => setFilters(prev => ({ ...prev, categoryNames: [], productNames: [], productIds: [] }))}
                 variant="ghost" 
                 size="sm"
                 className="text-xs text-blue-700 hover:text-blue-900"
               >
                 Clear All
               </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {filters.categoryNames?.map(categoryName => (
                <span
                  key={categoryName}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800"
                >
                  📂 {categoryName}
                </span>
              ))}
              {filters.productNames?.map(productName => (
                <span
                  key={productName}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                >
                  📦 {productName}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Date Range Display */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Report Period:</h4>
          <p className="text-sm text-gray-600">
            {moment(filters.startDate).format('YYYY-MM-DD')} to {moment(filters.endDate).format('YYYY-MM-DD') }
            <span className="ml-2 text-xs text-gray-500">
              (Week aligned: Monday to Sunday)
            </span>
          </p>
          {filters.regionConfigs && filters.regionConfigs.length > 0 && (
            <p className="text-sm text-gray-600 mt-1">
              Region Configs: {filters.regionConfigs.join(', ')}
            </p>
          )}
        </div>

        {/* Sales Table and Chart */}
        <WeeklySalesTable 
          salesData={salesData} 
          loading={loading} 
          searchTerm={searchTerm}
        />

        {/* Filter Modal */}
        <WeeklySalesFilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          filters={filters}
          onApplyFilters={handleFiltersChange}
          regionConfigOptions={regionConfigOptions}
        />
      </div>
    </div>
  );
};

export default WeeklySalesReport;
